import { useTranslation } from 'react-i18next'
import type { BookingToken } from './types'

interface Props {
	token: BookingToken
}

const LOCATION_LABEL_KEYS: Record<string, string> = {
	jobilla_meet: 'header.location.jobilla_meet',
	google_meet: 'header.location.google_meet',
	teams: 'header.location.teams',
	zoom: 'header.location.zoom',
	phone: 'header.location.phone',
	in_person: 'header.location.in_person',
	custom: 'header.location.custom',
}

export function BookingHeader({ token }: Props) {
	const { t } = useTranslation('booking')
	const locationLabel = token.meetingLocationType
		? t(LOCATION_LABEL_KEYS[token.meetingLocationType] as 'header.location.google_meet')
		: null

	return (
		<div className="flex flex-col gap-4">
			{token.companyLogoUrl
				? (
					<img
						src={token.companyLogoUrl}
						alt={token.companyName ?? t('header.company_logo_alt')}
						className="h-10 w-auto max-w-[120px] object-contain"
					/>
				)
				: null}

			<div>
				{token.companyName
					? <p className="text-sm font-medium text-gray-400">{token.companyName}</p>
					: null}
				{token.jobTitle
					? <h1 className="mt-1 text-xl font-semibold text-gray-900">{token.jobTitle}</h1>
					: null}
				{token.recruiterName
					? (
						<p className="mt-1 text-sm text-gray-500">
							{t('header.with_recruiter', { name: token.recruiterName })}
						</p>
					)
					: null}
			</div>

			<div className="flex flex-wrap gap-3">
				{token.durationMinutes
					? (
						<Chip>
							{token.durationMinutes >= 60
								? t('header.duration_hours', { count: token.durationMinutes / 60 })
								: t('header.duration_minutes', { count: token.durationMinutes })}
						</Chip>
					)
					: null}
				{locationLabel ? <Chip>{locationLabel}</Chip> : null}
				{token.meetingLocationType === 'in_person' && token.meetingLocationDetails
					? <Chip>{token.meetingLocationDetails}</Chip>
					: null}
			</div>

			{token.description
				? <p className="text-sm leading-relaxed text-gray-600">{token.description}</p>
				: null}
		</div>
	)
}

function Chip({ children }: { children: React.ReactNode }) {
	return (
		<span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
			{children}
		</span>
	)
}
